import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import { getMarketplaceUrl } from '../../config/platformConfig';
import {
  ArrowRight,
  BarChart3,
  Clock3,
  Download,
  ExternalLink,
  Globe,
  Layers3,
  Package,
  Search,
  Sparkles
} from 'lucide-react';
import './MerchantMarketplaceHub.css';

const TAB_ORDER = [
  { id: 'overview', label: 'Overview', path: '/merchant-marketplace/overview', icon: Globe },
  { id: 'boosts', label: 'Boost Products', path: '/merchant-marketplace/boosts', icon: Sparkles },
  { id: 'reports', label: 'Reports', path: '/merchant-marketplace/reports', icon: BarChart3 }
];

function formatMoney(value) {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0
    }).format(amount);
  } catch (_) {
    return `NGN ${amount.toLocaleString()}`;
  }
}

function getPrimaryImage(product) {
  if (product?.primaryImage) return product.primaryImage;
  if (Array.isArray(product?.gallery) && product.gallery.length) return product.gallery[0];
  return null;
}

function getActiveVariants(product) {
  // Backend serves the variant matrix under `product.variations.variants`
  // (see productServicePrisma). We fall back to the flat `product.variants`
  // only for older shapes that may still round-trip through here.
  const raw = Array.isArray(product?.variations?.variants)
    ? product.variations.variants
    : Array.isArray(product?.variants)
      ? product.variants
      : [];
  return raw.filter((variant) => variant && variant.enabled !== false && !variant.deletedAt);
}

function getQuantity(product) {
  if (product?.hasVariations) {
    return getActiveVariants(product).reduce((sum, variant) => {
      const next = Number(variant?.quantity ?? variant?.stock ?? variant?.inventory ?? 0);
      return sum + (Number.isFinite(next) && next > 0 ? next : 0);
    }, 0);
  }
  const baseQuantity = Number(product?.quantity ?? product?.stock ?? product?.inventory ?? 0);
  return Number.isFinite(baseQuantity) && baseQuantity > 0 ? baseQuantity : 0;
}

function isProductBoostEligible(product) {
  const productType = String(product?.productType || 'PHYSICAL').toUpperCase();
  if (productType !== 'PHYSICAL') return true;
  return getQuantity(product) > 0;
}

function getDisplayPrice(product) {
  const variants = getActiveVariants(product);
  const prices = variants
    .map((variant) => Number(variant?.price ?? variant?.salePrice ?? 0))
    .filter((price) => Number.isFinite(price) && price > 0);
  if (prices.length) return Math.min(...prices);
  return Number(product?.price || 0);
}

function getProductId(product) {
  return product?.id || product?._id || '';
}

function getCampaignStatusClass(status) {
  return `mmh-campaign-status mmh-campaign-status-${String(status || '').toLowerCase()}`;
}

function isCampaignBlocking(campaign) {
  const status = String(campaign?.status || '').toUpperCase();
  return status === 'ACTIVE' || status === 'QUEUED';
}

function formatCampaignDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeRemaining(endAt) {
  if (!endAt) return null;
  const end = new Date(endAt).getTime();
  if (!Number.isFinite(end)) return null;
  const diff = end - Date.now();
  if (diff <= 0) return 'Ended';
  const minutes = Math.floor(diff / 60000);
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  if (days >= 1) return `${days}d ${hours}h left`;
  if (hours >= 1) return `${hours}h ${minutes % 60}m left`;
  return `${minutes}m left`;
}

function MerchantMarketplaceHub({ initialTab = 'overview' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useSecureAuth();
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportTotals, setReportTotals] = useState(null);
  const [verifiedReference, setVerifiedReference] = useState('');

  const activeTab = useMemo(() => {
    const match = TAB_ORDER.find((tab) => location.pathname === tab.path);
    return match?.id || initialTab;
  }, [initialTab, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [configRes, productsRes] = await Promise.all([
          axiosInstance.get('/marketplace/config'),
          axiosInstance.get('/merchants/products', {
            params: { page: 1, limit: 100, status: 'ACTIVE' }
          })
        ]);

        if (cancelled) return;

        setConfig(configRes.data?.config || null);
        const merchantProducts = Array.isArray(productsRes.data)
          ? productsRes.data
          : (productsRes.data?.products || []);
        setProducts(merchantProducts);
      } catch (_) {
        if (!cancelled) {
          setConfig(null);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sponsoredConfig = config?.sponsoredProducts || {};
  const tierPricing = sponsoredConfig.tierPricing?.[user?.merchantTier || 'FREE'] || { discountPercent: 0, priorityWeight: 1 };
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((product) => {
      const hay = [
        product?.name,
        product?.slug,
        product?.sku,
        product?.category,
        product?.productType
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [products, search]);

  const baseBoostPrice = Number(sponsoredConfig.defaultPackage?.price || 0);
  const discountPercent = Number(tierPricing.discountPercent || 0);
  const discountedPrice = Math.max(0, baseBoostPrice - ((baseBoostPrice * discountPercent) / 100));
  const bundleConfig = sponsoredConfig.bundleDiscount || {};
  const bundleEnabled = Boolean(bundleConfig.enabled);
  const bundleMinProducts = Math.max(2, Number(bundleConfig.minProducts || 3));
  const bundleDiscountPercent = Math.max(0, Math.min(90, Number(bundleConfig.discountPercent || 0)));
  const openMarketplaceUrl = getMarketplaceUrl();
  const selectedProducts = useMemo(() => {
    const selectedIds = new Set(selectedProductIds);
    return products.filter((product) => selectedIds.has(getProductId(product)));
  }, [products, selectedProductIds]);
  const selectedCount = selectedProducts.length;
  const boostSubtotal = discountedPrice * selectedCount;
  const bundleQualifies = bundleEnabled && bundleDiscountPercent > 0 && selectedCount >= bundleMinProducts;
  const bundleDiscountAmount = bundleQualifies
    ? Math.round((boostSubtotal * bundleDiscountPercent) / 100)
    : 0;
  const boostTotal = Math.max(0, boostSubtotal - bundleDiscountAmount);
  const campaignByProductId = useMemo(() => {
    const rank = { ACTIVE: 4, QUEUED: 3, COMPLETED: 2, CANCELLED: 1 };
    const map = new Map();
    reports.forEach((campaign) => {
      if (!campaign?.productId) return;
      const current = map.get(campaign.productId);
      if (!current || (rank[campaign.status] || 0) > (rank[current.status] || 0)) {
        map.set(campaign.productId, campaign);
      }
    });
    return map;
  }, [reports]);

  const productById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const id = getProductId(product);
      if (id) map.set(id, product);
    });
    return map;
  }, [products]);

  const toggleSelectedProduct = (productId) => {
    if (!productId) return;
    const product = productById.get(productId);
    if (!product || !isProductBoostEligible(product)) return;
    const existingCampaign = campaignByProductId.get(productId);
    if (existingCampaign && isCampaignBlocking(existingCampaign)) return;
    setSelectedProductIds((current) => (
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    ));
  };

  useEffect(() => {
    if (!selectedProductIds.length) return;
    const filtered = selectedProductIds.filter((productId) => {
      const product = productById.get(productId);
      if (!product || !isProductBoostEligible(product)) return false;
      const campaign = campaignByProductId.get(productId);
      return !campaign || !isCampaignBlocking(campaign);
    });
    if (filtered.length !== selectedProductIds.length) {
      setSelectedProductIds(filtered);
    }
  }, [reports, campaignByProductId, productById, selectedProductIds]);

  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const { data } = await axiosInstance.get('/marketplace/boosts/reports');
      setReports(Array.isArray(data?.campaigns) ? data.campaigns : []);
      setReportTotals(data?.totals || null);
    } catch (_) {
      setReports([]);
      setReportTotals(null);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if ((activeTab === 'overview' || activeTab === 'reports' || activeTab === 'boosts') && !loading) {
      loadReports();
    }
  }, [activeTab, loading]);

  const boostStats = useMemo(() => {
    const stats = { active: 0, queued: 0, completed: 0, nextEndAt: null };
    reports.forEach((campaign) => {
      const status = String(campaign?.status || '').toUpperCase();
      if (status === 'ACTIVE') {
        stats.active += 1;
        if (campaign.endAt) {
          const endMs = new Date(campaign.endAt).getTime();
          if (Number.isFinite(endMs) && (!stats.nextEndAt || endMs < stats.nextEndAt)) {
            stats.nextEndAt = endMs;
          }
        }
      } else if (status === 'QUEUED') {
        stats.queued += 1;
      } else if (status === 'COMPLETED') {
        stats.completed += 1;
      }
    });
    return stats;
  }, [reports]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get('boost_reference') || params.get('reference');
    if (!reference || verifiedReference === reference) return;

    let cancelled = false;
    const verify = async () => {
      setVerifiedReference(reference);
      try {
        await axiosInstance.post('/marketplace/boosts/verify', { reference });
        if (!cancelled) {
          await loadReports();
          navigate('/merchant-marketplace/reports', { replace: true });
        }
      } catch (_) {
        if (!cancelled) navigate('/merchant-marketplace/reports', { replace: true });
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, verifiedReference]);

  const startBoostCheckout = async () => {
    if (!selectedProductIds.length || checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const { data } = await axiosInstance.post('/marketplace/boosts/checkout', {
        productIds: selectedProductIds,
        callbackUrl: `${window.location.origin}/merchant-marketplace/reports`
      });
      const paymentUrl = data?.payment?.authorization_url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      }
    } catch (error) {
      const blockedIds = error?.response?.data?.blockedProductIds;
      if (Array.isArray(blockedIds) && blockedIds.length) {
        setSelectedProductIds((current) => current.filter((id) => !blockedIds.includes(id)));
        loadReports();
      }
      toast.error(error?.response?.data?.message || 'Failed to start boost checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const downloadReportPdf = async () => {
    try {
      const response = await axiosInstance.get('/marketplace/boosts/reports.pdf', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marketplace-boost-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF report');
    }
  };

  const renderOverview = () => (
    <div className="mmh-stack">
      <section className="mmh-panel mmh-panel-hero">
        <div className="mmh-hero-copy">
          <span className="mmh-eyebrow">Marketplace Distribution</span>
          <h1>Reach more shoppers on the Revstar marketplace.</h1>
          <p>Boost products from your catalog to appear in front of active shoppers.</p>
          <div className="mmh-hero-actions">
            <button type="button" className="mmh-btn mmh-btn-primary" onClick={() => navigate('/merchant-marketplace/boosts')}>
              Choose products
            </button>
            <a className="mmh-btn mmh-btn-secondary" href={openMarketplaceUrl} target="_blank" rel="noreferrer">
              Open public marketplace <ExternalLink size={16} />
            </a>
          </div>
        </div>
        <div className="mmh-hero-grid">
          <div className="mmh-stat">
            <span>Visible sponsored slots</span>
            <strong>{Number(sponsoredConfig.slots || 0)}</strong>
          </div>
          <div className="mmh-stat">
            <span>Rotation window</span>
            <strong>{Number(sponsoredConfig.rotationHours || 0)}h</strong>
          </div>
          <div className="mmh-stat">
            <span>Your active products</span>
            <strong>{products.length}</strong>
          </div>
          <div className="mmh-stat">
            <span>Tier discount</span>
            <strong>{discountPercent}%</strong>
          </div>
        </div>
      </section>

      {reports.length ? (
        <section className="mmh-panel mmh-perf-card">
          <div className="mmh-perf-head">
            <div>
              <span className="mmh-eyebrow">Boost performance</span>
              <h2>How your campaigns are doing</h2>
            </div>
            <button
              type="button"
              className="mmh-btn mmh-btn-secondary"
              onClick={() => navigate('/merchant-marketplace/reports')}
            >
              View full report <ArrowRight size={14} />
            </button>
          </div>
          <div className="mmh-perf-grid">
            <div className="mmh-perf-stat mmh-perf-stat-active">
              <span>Active</span>
              <strong>{boostStats.active}</strong>
              <small>{boostStats.queued} in queue</small>
            </div>
            <div className="mmh-perf-stat mmh-perf-stat-impressions">
              <span>Impressions</span>
              <strong>{Number(reportTotals?.impressions || 0).toLocaleString()}</strong>
              <small>Across all campaigns</small>
            </div>
            <div className="mmh-perf-stat mmh-perf-stat-clicks">
              <span>Clicks</span>
              <strong>{Number(reportTotals?.clicks || 0).toLocaleString()}</strong>
              <small>{Number(reportTotals?.productViews || 0).toLocaleString()} product opens</small>
            </div>
            <div className="mmh-perf-stat mmh-perf-stat-ctr">
              <span>CTR</span>
              <strong>{Number(reportTotals?.ctr || 0)}%</strong>
              <small>
                {boostStats.nextEndAt
                  ? `Next ends in ${formatTimeRemaining(new Date(boostStats.nextEndAt).toISOString())}`
                  : 'No active campaigns'}
              </small>
            </div>
          </div>
        </section>
      ) : null}

      <section className="mmh-grid">
        <article className="mmh-panel">
          <div className="mmh-section-head">
            <Layers3 size={18} />
            <h2>How the marketplace works</h2>
          </div>
          <div className="mmh-flow">
            <div className="mmh-flow-item">
              <strong>Your storefront stays discoverable</strong>
              <p>Shoppers can always find your store and merchant profile on the marketplace.</p>
            </div>
            <div className="mmh-flow-item">
              <strong>Boosted products get priority visibility</strong>
              <p>Active boosts surface across high-traffic marketplace placements, putting your product in front of shoppers first.</p>
            </div>
            <div className="mmh-flow-item">
              <strong>Fair share of the spotlight</strong>
              <p>Every active campaign takes its turn in the visible placements, so exposure is shared instead of concentrated on one merchant.</p>
            </div>
          </div>
        </article>

        <article className="mmh-panel">
          <div className="mmh-section-head">
            <Clock3 size={18} />
            <h2>What you receive</h2>
          </div>
          <ul className="mmh-list">
            <li>Sponsored placement duration with clear start and end dates.</li>
            <li>Rotation logic when requests exceed visible sponsored inventory.</li>
            <li>Visibility metrics: impressions, clicks, product opens, and CTR.</li>
            <li>Merchant-first routing from marketplace into your live storefront.</li>
          </ul>
          <div className="mmh-note">
            {sponsoredConfig.reportNote || 'Boost improves visibility, not guaranteed sales.'}
          </div>
        </article>
      </section>
    </div>
  );

  const renderBoosts = () => (
    <div className="mmh-stack">
      <section className="mmh-grid mmh-grid-tight">
        <article className="mmh-panel">
          <div className="mmh-section-head">
            <Sparkles size={18} />
            <h2>Boost package</h2>
          </div>
          <div className="mmh-package">
            <strong>{sponsoredConfig.defaultPackage?.name || 'Marketplace boost'}</strong>
            <span>{Number(sponsoredConfig.defaultPackage?.durationDays || 0)} day run per product</span>
            <div className="mmh-package-price">
              <b>{formatMoney(discountedPrice)}</b>
              <small>
                Per product
                {discountPercent > 0 ? ` • ${formatMoney(baseBoostPrice)} base • ${discountPercent}% tier discount` : ''}
              </small>
            </div>
          </div>
        </article>

        <article className="mmh-panel">
          <div className="mmh-section-head">
            <Clock3 size={18} />
            <h2>Placement logic</h2>
          </div>
          <div className="mmh-inline-metrics">
            <span>{Number(sponsoredConfig.slots || 0)} visible slots</span>
            <span>{Number(sponsoredConfig.rotationHours || 0)}h rotation</span>
            <span>{String(sponsoredConfig.queuePolicy || 'FIFO')}</span>
          </div>
          <p className="mmh-panel-copy">
            Each selected product becomes its own sponsored campaign, with separate queue position, expiry, and performance reporting.
          </p>
        </article>
      </section>

      <section className="mmh-panel">
        <div className="mmh-toolbar">
          <div>
            <span className="mmh-eyebrow">Choose product</span>
            <h2>Select products to boost</h2>
          </div>
          <label className="mmh-search">
            <Search size={16} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, SKU, category"
            />
          </label>
        </div>

        <div className="mmh-product-grid">
          <div className="mmh-product-list">
            {filteredProducts.map((product) => {
              const productId = getProductId(product);
              const selected = selectedProductIds.includes(productId);
              const campaign = campaignByProductId.get(productId);
              const campaignBlocking = campaign ? isCampaignBlocking(campaign) : false;
              const outOfStock = !isProductBoostEligible(product);
              const isBlocked = campaignBlocking || outOfStock;
              const stockQuantity = getQuantity(product);
              const timeLeft = campaignBlocking && campaign?.status === 'ACTIVE'
                ? formatTimeRemaining(campaign.endAt)
                : null;
              const blockedTitle = outOfStock
                ? 'Out of stock — restock before boosting'
                : (campaignBlocking ? `Already ${String(campaign.status).toLowerCase()} — cannot boost again` : undefined);
              return (
                <button
                  key={productId}
                  type="button"
                  aria-pressed={selected}
                  aria-disabled={isBlocked}
                  disabled={isBlocked}
                  title={blockedTitle}
                  className={`mmh-product-card${selected ? ' is-selected' : ''}${isBlocked ? ' is-blocked' : ''}`}
                  onClick={() => toggleSelectedProduct(productId)}
                >
                  <span className="mmh-product-check" aria-hidden="true">{selected ? '✓' : ''}</span>
                  <div className="mmh-product-media">
                    {getPrimaryImage(product) ? <img src={getPrimaryImage(product)} alt={product.name} /> : <Package size={18} />}
                  </div>
                  <div className="mmh-product-copy">
                    <div className="mmh-product-name-row">
                      <strong>{product.name}</strong>
                      {outOfStock ? (
                        <span className="mmh-boost-badge mmh-boost-badge-oos">Out of stock</span>
                      ) : campaign ? (
                        <span className={`mmh-boost-badge mmh-boost-badge-${String(campaign.status || '').toLowerCase()}`}>
                          {campaign.status}
                        </span>
                      ) : null}
                    </div>
                    <span>{product.category || 'General'} • {product.productType || 'PHYSICAL'}</span>
                    <div className="mmh-product-meta">
                      <small>{formatMoney(getDisplayPrice(product))}</small>
                      <small>{stockQuantity} in stock</small>
                      {timeLeft ? <small className="mmh-product-timeleft">{timeLeft}</small> : null}
                    </div>
                  </div>
                </button>
              );
            })}
            {!filteredProducts.length ? (
              <div className="mmh-empty">
                <strong>No matching products</strong>
                <p>Try a different search term or publish more products before running a marketplace boost.</p>
              </div>
            ) : null}
          </div>

          <aside className="mmh-panel mmh-selection">
            <span className="mmh-eyebrow">Boost summary</span>
            {selectedCount ? (
              <>
                <h3>{selectedCount} product{selectedCount === 1 ? '' : 's'} selected</h3>
                <p>
                  Each product will run as a separate sponsored campaign, so reporting and rotation stay product-specific.
                </p>
                <div className="mmh-selection-total">
                  <span>Total due</span>
                  <strong>{formatMoney(boostTotal)}</strong>
                  <small>{formatMoney(discountedPrice)} x {selectedCount} product{selectedCount === 1 ? '' : 's'}</small>
                  {bundleQualifies ? (
                    <em className="mmh-bundle-line">
                      Bundle discount applied ({bundleDiscountPercent}% off {formatMoney(bundleDiscountAmount)})
                    </em>
                  ) : bundleEnabled && bundleDiscountPercent > 0 && selectedCount > 0 ? (
                    <em className="mmh-bundle-hint">
                      Add {bundleMinProducts - selectedCount} more to unlock {bundleDiscountPercent}% off
                    </em>
                  ) : null}
                </div>
                <div className="mmh-selected-list">
                  {selectedProducts.map((product) => (
                    <div key={getProductId(product)} className="mmh-selected-item">
                      <div>
                        <strong>{product.name}</strong>
                        <span>{product.category || 'General'} • {formatMoney(getDisplayPrice(product))}</span>
                      </div>
                      <button
                        type="button"
                        className="mmh-remove-selected"
                        onClick={() => toggleSelectedProduct(getProductId(product))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <label className="mmh-terms-check">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(event) => setTermsAccepted(event.target.checked)}
                  />
                  <span>
                    I agree to the{' '}
                    <button
                      type="button"
                      className="mmh-terms-link"
                      onClick={() => setTermsModalOpen(true)}
                    >
                      Marketplace Boost Terms
                    </button>
                    .
                  </span>
                </label>
                <button
                  type="button"
                  className="mmh-btn mmh-btn-primary"
                  onClick={startBoostCheckout}
                  disabled={checkoutLoading || !termsAccepted}
                >
                  {checkoutLoading ? 'Starting checkout...' : 'Continue to payment'} <ArrowRight size={16} />
                </button>
              </>
            ) : (
              <div className="mmh-empty">
                <strong>Select products</strong>
                <p>Pick one or more products from the list to see the boost total and campaign summary.</p>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );

  const renderReports = () => (
    <div className="mmh-stack">
      <section className="mmh-grid">
        <article className="mmh-panel">
          <div className="mmh-section-head">
            <BarChart3 size={18} />
            <h2>Report contents</h2>
          </div>
          <div className="mmh-metric-stack">
            <div className="mmh-metric-row mmh-metric-row-spend"><span>Spend</span><strong>{formatMoney(reportTotals?.spend || 0)}</strong></div>
            <div className="mmh-metric-row mmh-metric-row-impressions"><span>Impressions</span><strong>{Number(reportTotals?.impressions || 0).toLocaleString()}</strong></div>
            <div className="mmh-metric-row mmh-metric-row-clicks"><span>Clicks</span><strong>{Number(reportTotals?.clicks || 0).toLocaleString()}</strong></div>
            <div className="mmh-metric-row mmh-metric-row-ctr"><span>CTR</span><strong>{Number(reportTotals?.ctr || 0)}%</strong></div>
          </div>
        </article>

        <article className="mmh-panel">
          <div className="mmh-section-head">
            <Clock3 size={18} />
            <h2>Transparency rules</h2>
          </div>
          <ul className="mmh-list">
            <li>Campaign duration is fixed before the run starts.</li>
            <li>Rotation timing is published in advance.</li>
            <li>Queue handling should remain visible when inventory is tight.</li>
            <li>No promise of guaranteed sales, only measurable visibility.</li>
          </ul>
        </article>
      </section>

      <section className="mmh-panel">
        <div className="mmh-toolbar">
          <div>
            <span className="mmh-eyebrow">Campaign reports</span>
            <h2>Your boosted products</h2>
          </div>
          <button type="button" className="mmh-btn mmh-btn-secondary" onClick={downloadReportPdf}>
            <Download size={16} /> Download PDF
          </button>
        </div>
        {reportsLoading ? (
          <div className="mmh-empty">
            <strong>Loading boost reports</strong>
            <p>Pulling your marketplace campaign metrics.</p>
          </div>
        ) : reports.length ? (
          <div className="mmh-report-list">
            {reports.map((campaign) => {
              const startLabel = formatCampaignDate(campaign.startAt);
              const endLabel = formatCampaignDate(campaign.endAt);
              const timeLeft = campaign.status === 'ACTIVE' ? formatTimeRemaining(campaign.endAt) : null;
              const scheduleLabel = startLabel && endLabel
                ? `${startLabel} → ${endLabel}`
                : (campaign.status === 'QUEUED' ? 'Starts when a slot opens' : (startLabel || endLabel || null));
              const canRenew = campaign.status === 'COMPLETED' || campaign.status === 'CANCELLED';
              const productStillLive = productById.has(campaign.productId);
              return (
                <article key={campaign.id} className="mmh-report-row">
                  <div>
                    <strong>{campaign.productName}</strong>
                    <span>
                      <b className={getCampaignStatusClass(campaign.status)}>{campaign.status}</b>
                      <em>{campaign.packageName}</em>
                    </span>
                    {scheduleLabel ? (
                      <div className="mmh-report-schedule">
                        <Clock3 size={12} aria-hidden="true" />
                        <span>{scheduleLabel}</span>
                        {timeLeft ? <b>{timeLeft}</b> : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="mmh-report-metrics">
                    <span className="mmh-report-metric mmh-report-metric-impressions">{Number(campaign.impressions || 0).toLocaleString()} impressions</span>
                    <span className="mmh-report-metric mmh-report-metric-clicks">{Number(campaign.clicks || 0).toLocaleString()} clicks</span>
                    <span className="mmh-report-metric mmh-report-metric-opens">{Number(campaign.productViews || 0).toLocaleString()} opens</span>
                    <span className="mmh-report-metric mmh-report-metric-ctr">{Number(campaign.ctr || 0)}% CTR</span>
                    {canRenew && productStillLive ? (
                      <button
                        type="button"
                        className="mmh-report-renew"
                        onClick={() => {
                          setSelectedProductIds([campaign.productId]);
                          navigate('/merchant-marketplace/boosts');
                        }}
                      >
                        Renew boost
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="mmh-empty">
            <strong>No boost reports yet</strong>
            <p>Reports will appear here after a boost payment creates campaigns.</p>
          </div>
        )}
      </section>
    </div>
  );

  return (
    <div className="mmh-page">
      <header className="mmh-header">
        <div className="mmh-header-copy">
          <span className="mmh-eyebrow">Marketplace</span>
          <h1>Marketplace</h1>
          <p>
            Manage public marketplace visibility, product boosts, rotation rules, and reporting from one merchant workspace.
          </p>
        </div>
        <a className="mmh-link" href={openMarketplaceUrl} target="_blank" rel="noreferrer">
          View public marketplace <ExternalLink size={16} />
        </a>
      </header>

      <nav className="mmh-tabs" aria-label="Marketplace sections">
        {TAB_ORDER.map((tab) => {
          const ActiveIcon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <Link key={tab.id} to={tab.path} className={`mmh-tab${active ? ' is-active' : ''}`}>
              <ActiveIcon size={16} />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      {loading ? (
        <section className="mmh-panel">
          <div className="mmh-empty">
            <strong>Loading marketplace workspace</strong>
            <p>Pulling your catalog and current marketplace configuration.</p>
          </div>
        </section>
      ) : (
        <>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'boosts' && renderBoosts()}
          {activeTab === 'reports' && renderReports()}
        </>
      )}

      <Modal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Marketplace Boost Terms"
        size="lg"
        footer={(
          <div className="mmh-terms-footer">
            <button
              type="button"
              className="mmh-btn mmh-btn-secondary"
              onClick={() => setTermsModalOpen(false)}
            >
              Close
            </button>
            <button
              type="button"
              className="mmh-btn mmh-btn-primary"
              onClick={() => {
                setTermsAccepted(true);
                setTermsModalOpen(false);
              }}
            >
              I agree
            </button>
          </div>
        )}
      >
        <div className="mmh-terms-body">
          <p><strong>Last updated: {new Date().toLocaleDateString('en-NG', { month: 'long', day: 'numeric', year: 'numeric' })}</strong></p>
          <p>
            By purchasing a Marketplace Boost, you accept these terms. Read them carefully before continuing to payment.
          </p>
          <h4>1. What you are buying</h4>
          <p>
            A Marketplace Boost is a paid placement of your selected product on the Revstar marketplace surface for the duration shown on the checkout summary. It does not guarantee sales, orders, or any specific number of impressions, clicks, or conversions.
          </p>
          <h4>2. Payment and refunds</h4>
          <p>
            The full boost amount is charged at checkout. Once a campaign becomes active, the boost is non-refundable. If payment succeeds but no campaign is created due to a system error, the amount is refunded automatically.
          </p>
          <h4>3. Campaign lifecycle</h4>
          <p>
            Paid boosts enter a queue and become active as slots open. When active, each campaign runs for the fixed duration displayed at checkout and then completes automatically. Start and end dates for each campaign are visible in your Reports tab.
          </p>
          <h4>4. Product and content standards</h4>
          <p>
            Only active, in-stock products from your live storefront may be boosted. Products that are unavailable, out of stock, deleted, or restricted by Revstar policy may be paused or removed from the boost queue without a refund of the elapsed portion of the campaign.
          </p>
          <h4>5. Prohibited use</h4>
          <p>
            You agree not to use boosts to promote counterfeit, illegal, unsafe, or misleading products, or to violate Revstar's merchant policies. Revstar may cancel a boost at any time for a policy violation, and the merchant remains responsible for any actual costs incurred up to that point.
          </p>
          <h4>6. Reporting and metrics</h4>
          <p>
            Impressions, clicks, product opens, and click-through rate reflect activity on the Revstar marketplace surface only. Metrics are provided for merchant visibility and are not a warranty of accuracy. Small variations are expected.
          </p>
          <h4>7. Changes to these terms</h4>
          <p>
            Revstar may update these terms from time to time. Continued use of the Marketplace Boost feature after an update means you accept the revised terms.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default MerchantMarketplaceHub;
